import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Heart, Loader2, MapPin, Navigation, Share2 } from "lucide-react";
import { toast } from "sonner";

type HotSpot = {
  id: string;
  name: string;
  lat: number;
  lon: number;
  notes: string | null;
  photo_path: string | null;
  share_token: string;
  created_at: string;
  likes_count: number;
  photo_url?: string;
};

const photoUrl = (path: string | null) =>
  path ? supabase.storage.from("favourite-spot-photos").getPublicUrl(path).data.publicUrl : undefined;

const LIKED_KEY = "hotspots:liked";
const getLiked = (): Record<string, true> => {
  try { return JSON.parse(localStorage.getItem(LIKED_KEY) || "{}"); } catch { return {}; }
};

export const HotSpots = () => {
  const [items, setItems] = useState<HotSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState<Record<string, true>>(getLiked());

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("favourite_spots")
        .select("id,name,lat,lon,notes,photo_path,share_token,created_at,likes_count")
        .order("likes_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(60);
      if (!error && data) {
        setItems(data.map(s => ({ ...s, photo_url: photoUrl(s.photo_path) })));
      }
      setLoading(false);
    })();
  }, []);

  const like = async (id: string) => {
    if (liked[id]) {
      toast.info("You've already liked this spot");
      return;
    }
    const { data, error } = await supabase.rpc("increment_spot_likes", { spot_id: id });
    if (error) {
      toast.error("Couldn't register like");
      return;
    }
    const next = { ...liked, [id]: true as const };
    setLiked(next);
    localStorage.setItem(LIKED_KEY, JSON.stringify(next));
    setItems(prev => prev.map(s => s.id === id ? { ...s, likes_count: data ?? s.likes_count + 1 } : s));
  };

  const share = async (s: HotSpot) => {
    const url = `${window.location.origin}/spot/${s.share_token}`;
    const shareData = { title: s.name, text: `Check out ${s.name} on Swell Atlas`, url };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user cancelled */ }
  };

  const directions = (s: HotSpot) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="rounded-3xl bg-card/80 border border-border p-12 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-3xl bg-card/80 border border-border p-12 text-center text-muted-foreground">
        No hot spots yet. Be the first to add one from your profile.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-3xl text-foreground">Hot Spots</h2>
        <p className="text-sm text-muted-foreground">Community-shared surf spots — like the ones you love.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(s => (
          <article key={s.id} className="rounded-2xl bg-card border border-border overflow-hidden flex flex-col">
            <div className="aspect-[4/3] bg-muted relative">
              {s.photo_url ? (
                <img src={s.photo_url} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <MapPin className="h-8 w-8" />
                </div>
              )}
              <button
                onClick={() => like(s.id)}
                className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur transition ${liked[s.id] ? "bg-primary text-primary-foreground" : "bg-background/80 text-foreground hover:bg-background"}`}
                aria-label="Like spot"
              >
                <Heart className={`h-3.5 w-3.5 ${liked[s.id] ? "fill-current" : ""}`} />
                {s.likes_count}
              </button>
            </div>
            <div className="p-4 flex-1 flex flex-col gap-2">
              <h3 className="font-semibold text-foreground leading-tight">{s.name}</h3>
              <p className="text-xs text-muted-foreground">
                {s.lat.toFixed(3)}, {s.lon.toFixed(3)}
              </p>
              {s.notes && <p className="text-sm text-foreground/80 line-clamp-2">{s.notes}</p>}
              <div className="mt-auto flex gap-2 pt-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => directions(s)}>
                  <Navigation className="h-3.5 w-3.5 mr-1" /> Directions
                </Button>
                <Button size="sm" variant="outline" className="flex-1" onClick={() => share(s)}>
                  <Share2 className="h-3.5 w-3.5 mr-1" /> Share
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};